import axiosInstance from "../../../middlewares/axiosInstance";
import {
  APIResponse,
  ListRFQsRequest,
  ListRFQsResponse,
  GetAvailablePRsRequest,
  GetAvailablePRsResponse,
  CheckPRRFQRequest,
  CheckPRRFQResponse,
  CreateRFQRequest,
  CreateRFQResponse,
  UpdateRFQRequest,
  GetRFQRequest,
  SendRFQRequest,
  CancelRFQRequest,
  DeleteRFQRequest,
  GetRFQQuotationsRequest,
  AddVendorQuotationRequest,
  UpdateVendorQuotationRequest,
  AcceptVendorQuotationRequest,
  RejectVendorQuotationRequest,
  DeleteVendorQuotationRequest,
  GetRFQActivityLogsResponse,
  RFQ,
  VendorQuotation,
} from "../types/rfq.types";

class RFQService {
  private baseUrl = "/business-admin/rfq";

  // List all RFQs
  async listRFQs(
    params: ListRFQsRequest
  ): Promise<APIResponse<ListRFQsResponse>> {
    const response = await axiosInstance.post(`${this.baseUrl}/list`, params);

    // Handle the nested data structure from backend
    if (response.data?.success && response.data?.data?.data) {
      const transformedResponse = {
        success: response.data.success,
        data: {
          rfqs: response.data.data.data,
          totalCount: response.data.data.metadata?.total?.value || 0,
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
          totalPages: Math.ceil(
            (response.data.data.metadata?.total?.value || 0) / params.pageSize
          ),
        },
      };
      return transformedResponse;
    }

    return response.data;
  }

  // Get approved Purchase Requests that don't have RFQ yet
  async getAvailablePurchaseRequests(
    params: GetAvailablePRsRequest
  ): Promise<APIResponse<GetAvailablePRsResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/available-purchase-requests`,
      params
    );

    // Handle the nested data structure from backend
    if (response.data?.success && response.data?.data?.data) {
      const transformedResponse = {
        success: response.data.success,
        data: {
          purchaseRequests: response.data.data.data,
          totalCount: response.data.data.metadata?.total?.value || 0,
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
          totalPages: Math.ceil(
            (response.data.data.metadata?.total?.value || 0) / params.pageSize
          ),
        },
      };
      return transformedResponse;
    }

    return response.data;
  }

  // Check if Purchase Request already has RFQ
  async checkPRHasRFQ(
    params: CheckPRRFQRequest
  ): Promise<APIResponse<CheckPRRFQResponse>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/check-pr-rfq`,
      params
    );
    return response.data;
  }

  // Create new RFQ from approved Purchase Request
  async createRFQ(
    data: CreateRFQRequest
  ): Promise<APIResponse<CreateRFQResponse>> {
    const response = await axiosInstance.post(`${this.baseUrl}/create`, data);
    return response.data;
  }

  // Update existing RFQ
  async updateRFQ(data: UpdateRFQRequest): Promise<APIResponse<RFQ>> {
    const response = await axiosInstance.post(`${this.baseUrl}/update`, data);
    return response.data;
  }

  // Get single RFQ by ID
  async getRFQ(params: GetRFQRequest): Promise<APIResponse<RFQ>> {
    const response = await axiosInstance.post(`${this.baseUrl}/get`, params);
    return response.data;
  }

  // Send RFQ to suppliers
  async sendRFQ(params: SendRFQRequest): Promise<APIResponse<RFQ>> {
    const response = await axiosInstance.post(`${this.baseUrl}/send`, params);
    return response.data;
  }

  // Cancel RFQ
  async cancelRFQ(params: CancelRFQRequest): Promise<APIResponse<RFQ>> {
    const response = await axiosInstance.post(`${this.baseUrl}/cancel`, params);
    return response.data;
  }

  // Delete RFQ
  async deleteRFQ(params: DeleteRFQRequest): Promise<APIResponse<void>> {
    const response = await axiosInstance.post(`${this.baseUrl}/delete`, params);
    return response.data;
  }

  // Get RFQ quotations
  async getRFQQuotations(
    params: GetRFQQuotationsRequest
  ): Promise<APIResponse<VendorQuotation[]>> {
    const response = await axiosInstance.post(`${this.baseUrl}/quotations`, {
      _id: params._id,
    });
    return response.data;
  }

  // Vendor Quotation Management
  async addVendorQuotation(
    data: AddVendorQuotationRequest
  ): Promise<APIResponse<VendorQuotation>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/quotation/add`,
      data
    );
    return response.data;
  }

  async updateVendorQuotation(
    data: UpdateVendorQuotationRequest
  ): Promise<APIResponse<VendorQuotation>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/quotation/update`,
      data
    );
    return response.data;
  }

  async acceptVendorQuotation(
    params: AcceptVendorQuotationRequest
  ): Promise<APIResponse<VendorQuotation>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/quotation/accept`,
      params
    );
    return response.data;
  }

  async rejectVendorQuotation(
    params: RejectVendorQuotationRequest
  ): Promise<APIResponse<VendorQuotation>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/quotation/reject`,
      params
    );
    return response.data;
  }

  async deleteVendorQuotation(
    params: DeleteVendorQuotationRequest
  ): Promise<APIResponse<void>> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/quotation/delete`,
      params
    );
    return response.data;
  }

  // Get RFQ activity logs (from embedded data) - shows recent activities first
  getRFQActivityLogs(
    rfq: RFQ,
    pageIndex: number = 0,
    pageSize: number = 10
  ): GetRFQActivityLogsResponse {
    const activities = rfq.activity_logs || [];
    // Reverse the array to show recent activities first (newest to oldest)
    const reversedActivities = [...activities].reverse();
    const startIndex = pageIndex * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedActivities = reversedActivities.slice(startIndex, endIndex);

    return {
      activities: paginatedActivities,
      totalCount: activities.length,
      pageIndex,
      pageSize,
      totalPages: Math.ceil(activities.length / pageSize),
    };
  }
}

export const rfqService = new RFQService();
